import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { BaseAddressModel, BillToModel, CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React, { useEffect } from "react";
import { css } from "styled-components";

interface OwnProps {
    useBillTo: boolean;
    onUseBillToChange: (event: React.SyntheticEvent<Element, Event>, value: boolean) => void;
    billTo: BillToModel | undefined;
    address1: string;
    address1Ref?: React.Ref<HTMLInputElement>;
    onAddress1Change: (event: React.ChangeEvent<HTMLInputElement>) => void;
    address1Error?: string;
    country: string;
    countryRef?: React.Ref<HTMLSelectElement>;
    onCountryChange: (event: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => void;
    countryError?: string;
    state: string;
    stateRef?: React.Ref<HTMLSelectElement>;
    onStateChange: (event: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) => void;
    stateError?: string;
    city: string;
    cityRef?: React.Ref<HTMLInputElement>;
    onCityChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    cityError?: string;
    postalCode: string;
    postalCodeRef?: React.Ref<HTMLInputElement>;
    onPostalCodeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    postalCodeError?: string;
    availableCountries: CountryModel[];
    availableStates?: StateModel[] | null;
    extendedStyles?: CreditCardBillingAddressEntryStyles;
}

export interface CreditCardBillingAddressEntryStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    useBillingAddressGridItem?: GridItemProps;
    useBillingAddressCheckboxGroup?: CheckboxGroupComponentProps;
    useBillingAddressCheckbox?: CheckboxPresentationProps;
    billingAddressGridItem?: GridItemProps;
    billingAddressLabel?: TypographyPresentationProps;
    billingAddress?: AddressInfoDisplayStyles;
    address1GridItem?: GridItemProps;
    address1Text?: TextFieldPresentationProps;
    countryGridItem?: GridItemProps;
    countrySelect?: SelectPresentationProps;
    stateGridItem?: GridItemProps;
    stateSelect?: SelectPresentationProps;
    cityGridItem?: GridItemProps;
    cityText?: TextFieldPresentationProps;
    postalCodeGridItem?: GridItemProps;
    postalCodeText?: TextFieldPresentationProps;
    titleContainer?: InjectableCss;
    selectCard?: InjectableCss;
}

export const creditCardBillingAddressEntryStyles: CreditCardBillingAddressEntryStyles = {
    container: { 
        gap: 10,
        css: css`
            padding: 0.3125rem;

            label[class^="TypographyStyle"],label[class*="TypographyStyle"] {
                font-size: ${themeFontSizes.size14};
                font-weight: ${themeFontWeights.fontWeightMedium};
                font-family: 'Roboto',sans-serif;
                span {
                    padding-left: .9375rem;                        
                }
            }
            div[class^='FormFieldStyle'], div[class*=' FormFieldStyle'] {
                div[class^='FormInputWrapper'], div[class*=' FormInputWrapper']{
                    margin-top: 0.3125rem;
                }
            }

            .border-seperator {
                border-bottom: 0.0625rem solid ${themeColors.colorBorderBrands};
                padding-top: 5px;
                margin-bottom: 10px;
                width: 100%;
                display: block;
            }

            h3 {
                color: ${themeColors.colorHeadText};
                font-family: 'Roboto', sans-serif;
                font-size: ${themeFontSizes.size18};
                font-weight: ${themeFontWeights.fontWeightMedium};
                letter-spacing: 0;
                line-height: 1.4375rem;
            }
        `,
     },
    headingGridItem: { width: 12 },
    heading: { 
        variant: "h5",
        css: css `
            color: ${themeColors.colorHeadText};
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size18};
            font-weight: ${themeFontWeights.fontWeightMedium};
            letter-spacing: 0;
            line-height: 1.4375rem;
            margin-bottom: 0;
            margin-top: .9375rem;
        `
     },
    useBillingAddressGridItem: { 
        width: 12,
        css: css `
            color: ${themeColors.colorAddress} !important;
            font-family: 'Lato',sans-serif; !important;
            font-size: ${themeFontSizes.size15} !important;
            letter-spacing: 0.0106rem !important;
            line-height: 1.125rem;
            display: inline-flex;
            max-width: initial;
            flex-basis: initial;
            margin-top: -0.7rem !important;
            padding-left: 33px;

            label {
                color: ${themeColors.colorAddress};
                font-family: 'Lato',sans-serif !important;
                font-size: ${themeFontSizes.size15};
                letter-spacing: 0.0106rem;
                font-weight: ${themeFontWeights.fontWeightRegular} !important;
            }
        `
     },
    billingAddressGridItem: {
        width: 12,
        css: css`
            flex-direction: column;

            address {
                color: ${themeColors.colorAddress};
                font-family: 'Lato',sans-serif;
                font-size: ${themeFontSizes.size16};
                letter-spacing: 0;
                line-height: 1.5rem;
                margin-left:5px !important;

                p, span {
                    color: ${themeColors.colorAddress};
                }
            }

            span.border-seperator {
                padding-top: 0;
            }
        `,
    },
    billingAddressLabel: { 
        weight: 500,
        css: css`
            color: ${themeColors.colorHeadText};
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size18};
            font-weight: ${themeFontWeights.fontWeightMedium};
            letter-spacing: 0;
            line-height: 1.4375rem;
            margin-bottom: .9375rem;
        `,
     },
    address1GridItem: {
        width: 12,
        css: css`
            margin-left:5px !important;
        `,
    },
    countryGridItem: {
        width: 12,
        css: css`
            margin-left:5px !important;
        `,
    },
    stateGridItem: {
        width: [12,12,12,6,6],
        css: css`
            margin-left:5px !important;
        `,
    },
    cityGridItem: {
        width: 12,
        css: css`
            margin-left:5px !important;
        `,
    },
    postalCodeGridItem: {
        width: [12,12,12,6,6],
        css: css`
            margin-left:5px !important;
        `,
    },
    titleContainer: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: center;
            align-content: center;

            .select-card {
                color: ${themeColors.colorMidGrey};
                font-family: 'Roboto', sans-serif;
                font-size: ${themeFontSizes.size15};
                letter-spacing: 0;
                line-height: 1.3125rem;
                display: inline-block;
                vertical-align: middle;
                margin-right: .9375rem;

                @media screen and (max-width: 480px) {
                    max-width: 5.375rem;
                }
            }

            span {
                vertical-align: middle;
            }

            svg {
                width: 1.5rem;
                height: 1.5rem;
                scale: 0.8;
            }
        `,
    },
    selectCard: {
        css: css`
            width: 100%;
            flex-basis: 100%;
            max-width: 100%;
            position: relative;
            padding-left: .250625rem;

            @media screen and (max-width: 990px) {
                width: 100%;
                flex-basis: 100%;
                max-width: 100%;
            }

            .card-heading {
                color: ${themeColors.colorHeadText};
                font-family: 'Roboto', sans-serif;
                font-size: ${themeFontSizes.size18};
                font-weight: ${themeFontWeights.fontWeightMedium};
                letter-spacing: 0;
                line-height: 1.4375rem;
                margin-bottom: .9375rem;
                margin-top: 0.3125rem;
                @media screen and (min-width: 768px) {
                    padding-left: 0.3125rem;
                }
            }
        `,
    }, 
};

const CreditCardBillingAddressEntry = ({
    useBillTo,
    billTo,
    extendedStyles,
    onUseBillToChange,
    address1,
    address1Ref,
    onAddress1Change,
    address1Error,
    country,
    countryRef,
    onCountryChange,
    countryError,
    availableCountries,
    state,
    stateRef,
    onStateChange,
    stateError,
    city,
    cityRef,
    onCityChange,
    cityError,
    postalCode,
    postalCodeRef,
    onPostalCodeChange,
    postalCodeError,
}: OwnProps) => {
    const [styles] = React.useState(() => mergeToNew(creditCardBillingAddressEntryStyles, extendedStyles));

    if (!billTo) {
        return null;
    }

    const [selectedCountry, setSelectedCountry] = React.useState<CountryModel | undefined>(undefined);
    const [availableStates, setStates] = React.useState<StateModel[]>([]);
    
    useEffect(() => {
        setSelectedCountry(availableCountries.find(c => c.id === country));
    }, [country]);

    useEffect(() => {
        setStates(selectedCountry?.states || []);
    }, [selectedCountry]);

    const handleCountryChange: SelectProps["onChange"] = event => {
        const selectedCountry = availableCountries.find(c => c.id === event.target.value);
        setSelectedCountry(selectedCountry);
        onCountryChange(event, selectedCountry || null);
    };

    const handleStateChange: SelectProps["onChange"] = event => {
        const state = selectedCountry?.states?.find(s => s.id === event.target.value);
        onStateChange(event, state || null);
    };

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.headingGridItem} className="cc-address-heading">
                <Typography as="span" className="border-seperator"></Typography>
            </GridItem>
            <Typography {...styles.selectCard} as="div">
                <Typography {...styles.titleContainer} as="div">
                <Typography as="h3" className="card-heading" {...styles.billingAddressLabel}>{translate("Billing Address")}</Typography>

                <GridItem {...styles.useBillingAddressGridItem} className="use-bill-address">
                    <CheckboxGroup {...styles.useBillingAddressCheckboxGroup}>
                        <Checkbox
                            {...styles.useBillingAddressCheckbox}
                            checked={useBillTo}
                            onChange={onUseBillToChange}
                            data-test-selector="checkoutReviewAndSubmit_useBillingAddress"
                        >
                            {translate("Same as shipping address")}
                        </Checkbox>
                    </CheckboxGroup>
                </GridItem>
                </Typography>
            </Typography>
            {useBillTo ? (
                <GridItem {...styles.billingAddressGridItem}>
      
                    <AddressInfoDisplay
                        {...billTo}
                        state={billTo.state?.name}
                        country={billTo.country?.name}
                        extendedStyles={styles.billingAddress}
                    />
                </GridItem>
            ) : (
                <>
                    <GridItem {...styles.address1GridItem}>
                        <TextField
                            {...styles.address1Text}
                            label={translate("Address")}
                            value={address1}
                            onChange={onAddress1Change}
                            required
                            maxLength={30}
                            error={address1Error}
                            data-test-selector="checkoutReviewAndSubmit_creditCardBillingAddress1"
                            ref={address1Ref}
                        />
                    </GridItem>
                    <GridItem {...styles.countryGridItem}>
                        <Select
                            {...styles.countrySelect}
                            label={translate("Country")}
                            value={country}
                            onChange={handleCountryChange}
                            required
                            error={countryError}
                            data-test-selector="checkoutReviewAndSubmit_creditCardBillingCountry"
                            ref={countryRef}
                        >
                            <option value="">{translate("Select Country")}</option>
                            {availableCountries.map(country => (
                                <option key={country.id} value={country.id}>
                                    {country.name}
                                </option>
                            ))}
                        </Select>
                    </GridItem>
                    
                    <GridItem {...styles.cityGridItem}>
                        <TextField
                            {...styles.cityText}
                            label={translate("City")}
                            value={city}
                            onChange={onCityChange}
                            required
                            maxLength={30}
                            error={cityError}
                            data-test-selector="checkoutReviewAndSubmit_creditCardBillingCity"
                            ref={cityRef}
                        />
                    </GridItem>
                    {availableStates.length > 0 && (
                        <GridItem {...styles.stateGridItem}>
                            <Select
                                {...styles.stateSelect}
                                label={translate("State")}
                                value={state}
                                onChange={handleStateChange}
                                required
                                error={stateError}
                                data-test-selector="checkoutReviewAndSubmit_creditCardBillingState"
                                ref={stateRef}
                            >
                                <option value="">{translate("Select State")}</option>
                                {availableStates?.map(state => (
                                    <option key={state.id} value={state.id}>
                                        {state.name}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                    )}
                    <GridItem {...styles.postalCodeGridItem}>
                        <TextField
                            {...styles.postalCodeText}
                            label={translate("Postal Code")}
                            value={postalCode}
                            onChange={onPostalCodeChange}
                            required
                            maxLength={30}
                            error={postalCodeError}
                            data-test-selector="checkoutReviewAndSubmit_creditCardBillingPostalCode"
                            ref={postalCodeRef}
                        />
                    </GridItem>
                </>
            )}
        </GridContainer>
    );
};

export default CreditCardBillingAddressEntry;
